'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { ChevronDown } from 'lucide-react';

const HERO_IMAGE = '/design-images/full/1764858845413-0089DS1_9909-Enhanced-NR.jpg';

function renderIntroWithStyling(intro: string, comingSoon: string) {
  const renderedParts = [];
  let currentText = intro;

  const burgundyPhrase = '20 egyedi szoba és lakosztály';
  const italicPhrase = 'Hamarosan további 10 új szobával bővülünk.';
  const startPhrase = 'Válaszd ki ';

  let idx = 0;

  if (currentText.startsWith(startPhrase)) {
    renderedParts.push(
      <span key={idx++} className="font-semibold text-[#801515] tracking-wide uppercase">
        {startPhrase}
      </span>
    );
    currentText = currentText.slice(startPhrase.length);
  }

  const burgundyIndex = currentText.indexOf(burgundyPhrase);
  if (burgundyIndex !== -1) {
    renderedParts.push(
      <span key={idx++}>{currentText.slice(0, burgundyIndex)}</span>
    );
    renderedParts.push(
      <span key={idx++} className="font-semibold text-[#801515]">
        {burgundyPhrase}
      </span>
    );
    currentText = currentText.slice(burgundyIndex + burgundyPhrase.length);
  }

  const italicIndex = currentText.indexOf(italicPhrase);
  if (italicIndex !== -1) {
    renderedParts.push(
      <span key={idx++}>{currentText.slice(0, italicIndex)}</span>
    );
    renderedParts.push(
      <span key={idx++} className="italic">
        {italicPhrase}
      </span>
    );
    currentText = currentText.slice(italicIndex + italicPhrase.length);
  }

  if (currentText) {
    renderedParts.push(<span key={idx++}>{currentText}</span>);
  }

  if (comingSoon) {
    renderedParts.push(
      <span key={idx++} className="italic">
        {' '}{comingSoon}
      </span>
    );
  }

  return renderedParts;
}

function renderBookingGuideWithStyling(bookingGuide: string) {
  const burgundyPhrases = ['érkezés és távozás dátumát'];
  const regularHighlights = ['FOGLALÁSA'];

  const renderedParts = [];
  let currentText = bookingGuide;
  let idx = 0;

  for (const phrase of burgundyPhrases) {
    const phraseIndex = currentText.indexOf(phrase);
    if (phraseIndex !== -1) {
      const before = currentText.slice(0, phraseIndex);
      const after = currentText.slice(phraseIndex + phrase.length);

      const beforeParts = [];
      let beforeText = before;

      for (const highlight of regularHighlights) {
        const highlightIndex = beforeText.indexOf(highlight);
        if (highlightIndex !== -1) {
          beforeParts.push(<span key={idx++}>{beforeText.slice(0, highlightIndex)}</span>);
          beforeParts.push(
            <span key={idx++} className="font-semibold text-[#801515]">
              {highlight}
            </span>
          );
          beforeText = beforeText.slice(highlightIndex + highlight.length);
        }
      }
      if (beforeText) {
        beforeParts.push(<span key={idx++}>{beforeText}</span>);
      }

      renderedParts.push(...beforeParts);
      renderedParts.push(
        <span key={idx++} className="font-semibold text-[#801515]">
          {phrase}
        </span>
      );
      currentText = after;
      break;
    }
  }

  if (currentText) {
    for (const highlight of regularHighlights) {
      const highlightIndex = currentText.indexOf(highlight);
      if (highlightIndex !== -1) {
        renderedParts.push(<span key={idx++}>{currentText.slice(0, highlightIndex)}</span>);
        renderedParts.push(
          <span key={idx++} className="font-semibold text-[#801515]">
            {highlight}
          </span>
        );
        currentText = currentText.slice(highlightIndex + highlight.length);
      }
    }
  }

  if (currentText) {
    renderedParts.push(<span key={idx++}>{currentText}</span>);
  }

  return renderedParts;
}

export function RoomsHero() {
  const t = useTranslations('roomsHero');
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* ━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src={HERO_IMAGE}
            alt={t('imageAlt')}
            fill
            className="object-cover image-breathe-slow"
            priority
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70" />

        <div
          className={`relative h-full flex flex-col items-center justify-center px-6 text-center transition-all duration-[2000ms] ease-out ${
            heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h1
            className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight"
            style={{ textShadow: '2px 2px 30px rgba(0,0,0,0.5)' }}
          >
            {t('title')}
          </h1>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div
              className="h-[2px] w-20 bg-[#801515]"
              style={{ animation: 'pulse 3s ease-in-out infinite' }}
            />
            <p
              className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90"
              style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}
            >
              {t('subtitle')}
            </p>
            <div
              className="h-[2px] w-20 bg-[#801515]"
              style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }}
            />
          </div>
          <p
            className="text-xl sm:text-3xl md:text-4xl text-white/95 max-w-3xl mx-auto font-light"
            style={{ textShadow: '1px 1px 8px rgba(0,0,0,0.5)' }}
          >
            {t('tagline')}
          </p>

          {/* Scroll indicator */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50">
            <span className="text-xs uppercase tracking-widest">{t('discover')}</span>
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </div>
        </div>
      </section>

      {/* ━━━ INTRO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section id="choose-feeling" className="bg-[#faf8f6] py-16 md:py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg md:text-xl leading-relaxed text-gray-700 mb-8">
            {renderIntroWithStyling(t('intro'), t('comingSoon'))}
          </p>

          <p className="text-lg md:text-xl leading-relaxed text-gray-700">
            {renderBookingGuideWithStyling(t('bookingGuide'))}
          </p>
        </div>
      </section>

    </>
  );
}
